import React, { useState } from 'react';
import { Package, Loader2, Edit, Trash2, Copy, Upload, CheckCircle, Clock, User, ClipboardList, X } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { PlansEditModal } from '../plans/PlansEditModal';
import { PlansProcedures } from '../plans/PlansProcedures';
import { PlansSpareParts } from '../plans/PlansSpareParts';
import { WorkOrdersEditModal } from '../work-orders/WorkOrdersEditModal';
import { ExecutionTasks } from './ExecutionTasks';
import { ExecutionTeam } from './ExecutionTeam';

export const ExecutionDetails = ({ product, loading, onTaskStart, onFinish, isSuperior, userId /*onSaveDuplicate*/ }) => {
  const [activeTab, setActiveTab] = useState('details');

  let nombres = {
    nombre_asset: product?.asset_location || null,
    nombre_sensor_asset: product?.sensor_asset_location || null,
  };

  const dateFormat = (string) => {
    if (!string) return null;
    const date = string.split('T')[0];
    const clock = string.split('T')[1];
    const [year, month, day] = date.split('-');
    const [hour, minute] = clock.split(':');
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="flex flex-col items-center text-gray-400">
          <Loader2 className="animate-spin mb-4" size={32} />
          {<p>Loading work order details...</p>}
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Select a work order to view its details</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg flex flex-col max-h-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-400" />
            <h2 className="text-xl font-semibold text-white">
              Work Order Details
            </h2>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="sticky top-0 z-10 px-6 border-b border-gray-700 bg-gray-800">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'details'
                ? 'text-blue-400'
                : 'text-gray-400 hover:text-gray-200'
                }`}
            >
              Details
              {activeTab === 'details' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>

            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'tasks'
                ? 'text-blue-400'
                : 'text-gray-400 hover:text-gray-200'
                }`}
            >
              Tasks
              {activeTab === 'tasks' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>

            <button
              onClick={() => setActiveTab('assignments')}
              className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'assignments'
                ? 'text-blue-400'
                : 'text-gray-400 hover:text-gray-200'
                }`}
            >
              Team
              {activeTab === 'assignments' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>

          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <section>
                <div className="flex gap-4 justify-between">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2 rounded-lg">
                    <Package className="w-5 h-5 text-gray-400" />
                    Basic Information
                  </h3>
                  <div className="flex inline-flex items-center mb-4 gap-2">
                    {product.quality_check !== null && (
                      <>
                        <p className='text-blue-200/60 font-medium text-xs whitespace-nowrap'>Quality Check</p>
                        {product.quality_check === true && (
                          <CheckCircle className="text-green-400" size={20} />
                        )}
                        {product.quality_check === false && (
                          <X className="text-red-500" size={20} />
                        )}
                      </>)}
                    <p className="text-blue-200/60 font-medium text-xs">Status</p>
                    <Badge className={
                      product.status?.toLowerCase() === 'completed' ? 'bg-green-900/50 text-green-200' :
                        product.status?.toLowerCase() === 'in_progress' ? 'bg-yellow-900/50 text-yellow-200' :
                          product.status?.toLowerCase() === 'cancelled' ? 'bg-red-900/50 text-red-200' :
                            'bg-blue-900/50 text-blue-200'
                    }>
                      {product.status?.charAt(0).toUpperCase() + product.status?.slice(1) || 'N/A'}
                    </Badge>
                  </div>
                </div>
                {product.completion_notes && (
                  <div className={`bg-green-950/20 border border-green-800/40 rounded-xl p-4 mb-4 ${product.quality_check === false ? ' border-red-500/50 bg-red-950/20' : ''}`}>
                    <p className={`${product.quality_check === false ? 'text-red-400' : 'text-green-400'} text-xs font-semibold mb-1`}>Completion Notes</p>
                    <p className="text-white whitespace-pre-wrap">{product.completion_notes}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-700/50 rounded-lg p-4 overflow-auto">
                    <p className="text-xs text-gray-400 mb-1">Title</p>
                    <p className="text-white font-medium overflow-auto">{product?.title || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Asset</p>
                    <Badge className="bg-violet-900/50 text-violet-200">
                      {product?.asset_name || 'N/A'}
                    </Badge>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Work Order Type</p>
                    <p className="text-white">
                      {product.work_order_type?.charAt(0).toUpperCase() + product.work_order_type?.slice(1) || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Priority</p>
                    <Badge className={
                      product.priority?.toLowerCase() === 'critical' ? 'bg-red-900/50 text-red-200' :
                        product.priority?.toLowerCase() === 'high' ? 'bg-orange-900/50 text-orange-200' :
                          product.priority?.toLowerCase() === 'medium' ? 'bg-yellow-900/50 text-yellow-200' :
                            'bg-green-900/50 text-green-200'
                    }>
                      {product.priority?.charAt(0).toUpperCase() + product.priority?.slice(1) || 'N/A'}
                    </Badge>
                  </div>
                  {product.description && (
                    <div className="bg-gray-700/50 rounded-lg p-4 md:col-span-2">
                      <p className="text-xs text-gray-400 mb-1">Description</p>
                      <p className="text-white whitespace-pre-wrap">{product.description}</p>
                    </div>
                  )}
                </div>

                <div className="bg-gray-700/50 rounded-xl p-4 mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="pl-3 border-l-2 border-blue-500">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Scheduled For</p>
                    <p className="text-sm font-semibold text-white mt-1">{dateFormat(product.scheduled_date) || 'N/A'}</p>
                  </div>

                  <div className="pl-3 border-l-2 border-amber-500">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Started At</p>
                    <p className="text-sm font-semibold text-white mt-1">
                      {product.start_date ? dateFormat(product.start_date) : <span className="text-gray-500 font-normal">—</span>}
                    </p>
                  </div>

                  <div className="pl-3 border-l-2 border-emerald-500">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Finished At</p>
                    <p className="text-sm font-semibold text-white mt-1">
                      {product.end_date ? dateFormat(product.end_date) : <span className="text-gray-500 font-normal">—</span>}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className={`bg-gray-700/50 rounded-lg p-4 ${product.status !== 'completed' ? 'md:col-span-2' : 'md:col-span-1'}`}>
                    <p className="text-xs text-gray-400 mb-1">Estimated Duration</p>
                    <p className="text-white">
                      {`${product.estimated_hours} hours`}
                    </p>
                  </div>
                  {product.status === 'completed' && (
                    <div className="bg-green-950/20  border border-green-800/40 rounded-lg p-4">
                      <p className="text-xs text-gray-400 mb-1">Actual Duration</p>
                      <p className="text-white">
                        {`${product.actual_hours} hours`}
                      </p>
                    </div>
                  )}
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Created at:</p>
                    <p className="text-white">{dateFormat(product.created_at) || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Updated at:</p>
                    <p className="text-white">{dateFormat(product.updated_at) || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Created by:</p>
                    <p className="text-white">{product.creator_name}</p>
                  </div>
                </div>
              </section>
            </div>
          ) : activeTab === 'tasks' ? (
            <ExecutionTasks workOrder={product}
              dateFormat={dateFormat}
              onTaskStart={onTaskStart}
              onFinish={onFinish}
              isSuperior={isSuperior}
              userId={userId}
            />
          ) : (
            <>
              <ExecutionTeam workOrder={product} dateFormat={dateFormat} />
            </>
          )}
        </div>
      </div>
    </>
  );
};